/**
 * Profile Widget - Embeddable Bundle Entry Point
 *
 * Replaces the paginated /my-profile/ page of stock MP widgets on
 * woodsidebible.org with ONE scrolling page: the person, their household
 * and family, campus, groups, giving, invoices, subscriptions, privacy and
 * the account chores. Renders inside a shadow root for style isolation
 * from the WordPress theme (see shell.tsx).
 *
 * Embed:
 *   <div id="profile-root"></div>
 *   <script src="https://widgets.woodsidebible.org/profile/widget.js"></script>
 *
 *   <div id="profile-root"
 *        data-group-finder-url="https://woodsidebible.org/group-finder/"
 *        data-give-url="https://woodsidebible.org/give/"
 *        data-pay-url="https://woodsidebible.org/pay/"
 *        data-google-maps-api-key="…"></div>
 *
 * The map key defaults to the site's own (the one the locations finder
 * bakes in); `data-google-maps-api-key` or PROFILE_WIDGET_CONFIG override it.
 */

import React from 'react';
import { ProfileWidget } from '../src/components/ProfileWidget';
import { attr } from './params';

/** A positive integer data-attribute, else undefined. */
function num(raw: string | undefined): number | undefined {
  const n = Number(raw);
  return Number.isInteger(n) && n > 0 ? n : undefined;
}
import { ShadowWidget } from './shell';
import { definedSettings } from '@/lib/settings';

interface WidgetConfig {
  apiBaseUrl?: string;
  containerId?: string;
  /** Run against the in-memory family instead of apps/api (previews only) */
  mock?: boolean;
  groupFinderUrl?: string;
  giveUrl?: string;
  payUrl?: string;
  /** Online Giving's donor sign-in, for "Manage in Online Giving" */
  manageGivingUrl?: string;
  googleMapsApiKey?: string;
  /** The Pledge_Campaigns row the See Jesus page writes commitments to (default 194) */
  seeJesusCampaignId?: number;
  /** Blur names, faces, contact details, addresses and money, for demos and recordings; `?blur=1` on the page does the same */
  blur?: boolean;
}

declare global {
  interface Window {
    PROFILE_WIDGET_CONFIG?: WidgetConfig;
    ProfileWidget?: ShadowWidget;
  }
}

const DEFAULT_CONTAINER_ID = 'profile-root';
/** woodsidebible.org's Maps key (GCP project "Woodside Website"), the same one the locations finder ships. */
const DEFAULT_MAPS_KEY = 'AIzaSyBzf7AATUxLctt3O69OqPVbi3owcCWp17Y';

/** `?blur=1` / `?blur=true` on the page, or `data-blur` on the container, or `blur` in the config. */
function blurWanted(config: WidgetConfig): boolean {
  if (config.blur !== undefined) return config.blur;
  const q = new URLSearchParams(window.location.search).get('blur');
  if (q !== null) return q === '1' || q === 'true' || q === '';
  const el = document.getElementById(config.containerId || DEFAULT_CONTAINER_ID);
  const d = el?.dataset.blur;
  return d === '1' || d === 'true' || d === '';
}

function createProfileWidget(config: WidgetConfig): ShadowWidget {
  const apiBaseUrl = config.apiBaseUrl || 'https://api.woodsidebible.org';
  const blur = blurWanted(config);
  return new ShadowWidget({
    containerIds: config.containerId ? [config.containerId] : [DEFAULT_CONTAINER_ID],
    overlayHostId: 'profile-overlay',
    fontId: 'profile-widget-fonts',
    scrollLockFlag: '__PROFILE_WIDGET_SCROLL_LOCK_PREVENTED__',
    label: 'Profile Widget',
    hostClass: blur ? 'blur-pii' : undefined,
    render: (container) => (
      // Precedence: explicit config object > data-* attributes > MP's
      // CUSTOMWIDGETS settings (fetched at boot) > the defaults in lib/settings.
      <ProfileWidget
        apiBaseUrl={apiBaseUrl}
        mock={config.mock ?? false}
        settings={definedSettings({
          groupFinderUrl: config.groupFinderUrl ?? attr(container.dataset.groupFinderUrl),
          giveUrl: config.giveUrl ?? attr(container.dataset.giveUrl),
          payUrl: config.payUrl ?? attr(container.dataset.payUrl),
          manageGivingUrl: config.manageGivingUrl ?? attr(container.dataset.manageGivingUrl),
          seeJesusCampaignId:
            config.seeJesusCampaignId ?? num(container.dataset.seeJesusCampaignId),
        })}
        googleMapsApiKey={
          config.googleMapsApiKey ?? attr(container.dataset.googleMapsApiKey) ?? DEFAULT_MAPS_KEY
        }
        blurPii={blur}
      />
    ),
  });
}

// Auto-initialize
if (typeof window !== 'undefined') {
  // API base is the centralized api.woodsidebible.org host, baked at build
  // time from CHURCH_HUB_API_URL. (Do NOT derive it from the script origin:
  // the script is served from widgets.woodsidebible.org, which has no /api
  // routes.) window.PROFILE_WIDGET_CONFIG can override for local testing.
  const config: WidgetConfig = {
    apiBaseUrl: process.env.CHURCH_HUB_API_URL || 'https://api.woodsidebible.org',
    ...(window.PROFILE_WIDGET_CONFIG || {}),
  };
  window.PROFILE_WIDGET_CONFIG = config;

  // Skip if already initialized (e.g. script loaded twice via bfcache)
  if (!window.ProfileWidget) {
    const boot = () => {
      const widget = createProfileWidget(config);
      widget.init();
      window.ProfileWidget = widget;
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
}

export default ShadowWidget;
